package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.observability.AnalyticsEvent
import com.evoapps.evoshift.observability.AppTelemetry

import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.capabilitiesAt
import com.evoapps.evoshift.domain.model.NotFoundException
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.staffing.LabourTarget
import com.evoapps.evoshift.domain.budget.BudgetComparison
import com.evoapps.evoshift.domain.budget.LabourBudget
import com.evoapps.evoshift.domain.usecase.finance.GetLabourBudgetUseCase
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.error_user_not_found
import evoshift.composeapp.generated.resources.financials_no_roster
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.Roster
import com.evoapps.evoshift.domain.model.RosterFinancials
import com.evoapps.evoshift.domain.model.CostSourceType
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.model.FixedCost
import com.evoapps.evoshift.domain.model.MonthlyResult
import com.evoapps.evoshift.domain.explain.Breakdown
import com.evoapps.evoshift.domain.explain.MonthExplanations
import com.evoapps.evoshift.domain.explain.Ratio
import com.evoapps.evoshift.domain.explain.Side
import com.evoapps.evoshift.domain.model.LedgerKind
import com.evoapps.evoshift.domain.usecase.finance.AddOneOffCostUseCase
import com.evoapps.evoshift.domain.usecase.finance.DeleteOneOffCostUseCase
import com.evoapps.evoshift.domain.usecase.finance.OneOffCostInput
import evoshift.composeapp.generated.resources.one_off_cost_deleted
import evoshift.composeapp.generated.resources.one_off_cost_saved
import com.evoapps.evoshift.domain.repository.FinancialRepository
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.roster.GetRosterSummaryUseCase
import com.evoapps.evoshift.session.WorkplaceSessionManager
import com.evoapps.evoshift.session.WorkplaceMonthSelection
import com.evoapps.evoshift.session.of
import kotlinx.datetime.YearMonth
import kotlinx.datetime.todayIn
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.usecase.owner.SharedCostChanges
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch
import kotlinx.coroutines.async
import com.evoapps.evoshift.domain.usecase.finance.withLiveLabour
import com.evoapps.evoshift.domain.usecase.finance.spmh
import kotlinx.datetime.TimeZone
import kotlinx.datetime.number
import kotlinx.datetime.toLocalDateTime
import com.evoapps.evoshift.domain.util.AppClock.now

/** The ledger's order: by day, then the rules' costs before the one-off expenses, then by name. */
internal fun List<FixedCost>.sortedForLedger(): List<FixedCost> =
    sortedWith(compareBy<FixedCost>({ it.date }, { it.isOneOff }, { it.name }))

/** The month's figures with [delta] more other costs (plan, actual and combined: a cost is in all three). */
internal fun RosterFinancials.withOtherCosts(delta: Double): RosterFinancials = withLedger(delta, 0.0)

/** The month's figures with [costs] more other costs and [revenue] more other revenue (SH-87), in all three. */
internal fun RosterFinancials.withLedger(costs: Double, revenue: Double): RosterFinancials = copy(
    planned = planned.copy(otherCosts = planned.otherCosts + costs, otherRevenue = planned.otherRevenue + revenue),
    actual = actual.copy(otherCosts = actual.otherCosts + costs, otherRevenue = actual.otherRevenue + revenue),
    combined = combined.copy(otherCosts = combined.otherCosts + costs, otherRevenue = combined.otherRevenue + revenue)
)

/** The ledger item [cost] added (+1) or taken away (−1) from the month's figures, by its kind. */
internal fun RosterFinancials.withItem(cost: FixedCost?, sign: Int): RosterFinancials {
    if (cost == null) return this
    val amount = cost.amount * sign
    return if (cost.isRevenue) withLedger(0.0, amount) else withLedger(amount, 0.0)
}

/** Labour % of sales above which the Money tab shows it red when the workplace's target is unknown. */
private const val FALLBACK_LABOUR_LIMIT = 35.0

data class FinancialsUiState(
    val isLoading: Boolean = true,
    val workplaceName: String = "",
    /** The workplace's currency, for every amount on the screen. */
    val currency: String = DEFAULT_CURRENCY,
    val rosterName: String = "",
    val financials: RosterFinancials? = null,
    val rosters: List<Roster> = emptyList(),
    val selectedRosterId: String? = null,
    val availableWorkplaceIds: Map<String, String> = emptyMap(),
    val currentWorkplaceId: String? = null,
    /** What the person may do at this workplace: the figures need VIEW_FINANCES, payroll PAYROLL. */
    val capabilities: Set<Capability> = emptySet(),
    /**
     * The month's ledger (rosters/{id}/costs, SH-84): the costs its recurring rules generated and
     * its one-off expenses, by date.
     */
    val costs: List<FixedCost> = emptyList(),
    /** SH-95: the workplace's labour target (% of income); null when unknown. */
    val labourTarget: LabourTarget? = null,
    /** SH-115: the workplace's labour budget; null when unknown, [LabourBudget.OFF] while off. */
    val labourBudget: LabourBudget? = null,
    /**
     * SH-152: the month's days as worked out now (the live labour cost is added up from them),
     * for the figures' explanations person by person; empty when not readable.
     */
    val days: List<com.evoapps.evoshift.domain.usecase.finance.DayTrend> = emptyList(),
    /** SH-152: the team's names by user id, for the explanations' person terms. */
    val names: Map<String, String> = emptyMap(),
    val error: UiText? = null,
    /** SH-168: the month's figures could not be read: Try again instead of "nothing yet". */
    val loadError: UiText? = null,
    val successMessage: UiText? = null,
    /** SH-225: the month's sales against last week (week to date) and last year (month to date); null without data. */
    val salesComparison: com.evoapps.evoshift.domain.usecase.finance.DashboardSalesComparison? = null
) {
    /**
     * SH-95: whether [percent] (labour % of income) is above the target band; without a known
     * target, above the market's usual 35% of sales.
     */
    fun labourOverTarget(percent: Double): Boolean = percent > (labourTarget?.high ?: FALLBACK_LABOUR_LIMIT)

    /**
     * SH-46: the month's result, plan against actual: the totals' revenue and labour cost with the
     * ledger's fixed costs and one-off expenses (so an expense being saved counts at once).
     */
    val monthlyResult: MonthlyResult? get() = financials?.let { MonthlyResult.of(it, costs) }

    // --- SH-152: how each figure on the screen was calculated --------------------------------

    /** [side]'s labour % (of income, or of sales without a commission). */
    fun labourPercentExplained(side: Side): Ratio? = financials?.let { MonthExplanations.labourPercent(side, it, days) }

    /** SH-191: [side]'s SPMH: the days' net sales ÷ their hours (worked out from [days]); null without them. */
    fun spmh(side: Side): Double? = days.spmh(side)

    /** SH-191: how [side]'s SPMH was worked out. */
    fun spmhExplained(side: Side): com.evoapps.evoshift.domain.explain.PerHour? =
        com.evoapps.evoshift.domain.explain.SpmhExplanations.days(side, days)

    /** [side]'s labour cost, person by person. */
    fun labourCostExplained(side: Side): Breakdown? = financials?.let { f ->
        with(MonthExplanations) { labourCost(side, f.summaryOf(side).laborCost, days) }
    }

    /** [side]'s net sales: takings and targets. */
    fun netSalesExplained(side: Side): Breakdown? = financials?.let { f ->
        with(MonthExplanations) { netSales(side, f.summaryOf(side).revenue, days) }
    }

    /** [side]'s ledger costs, item by item. */
    fun otherCostsExplained(side: Side): Breakdown? = financials?.let { f ->
        with(MonthExplanations) { otherCosts(side, f.summaryOf(side).otherCosts, costs) }
    }

    /** [side]'s labour cost plus the other costs. */
    fun totalExpensesExplained(side: Side): Breakdown? = financials?.let { f ->
        with(MonthExplanations) { totalExpenses(side, f.summaryOf(side), days, costs) }
    }

    /** The month's result card, row by row, per side. */
    fun resultExplained(side: Side): MonthExplanations.ResultSide? =
        monthlyResult?.let { MonthExplanations.resultSide(it, side, costs, days) }

    /** SH-115: the month's labour budget against its planned and actual labour cost; null while off. */
    val budgetComparison: BudgetComparison? get() {
        val budget = labourBudget ?: return null
        val figures = financials ?: return null
        return BudgetComparison.of(budget, figures.planned.laborCost, figures.actual.laborCost)
    }
}

sealed class FinancialsEvent {
    data class SelectRoster(val rosterId: String) : FinancialsEvent()
    data class SelectWorkplace(val workplaceId: String) : FinancialsEvent()
    /** Adds a one-off expense to the month, or changes one ([editingId]). */
    data class SaveOneOffCost(val input: OneOffCostInput, val editingId: String? = null) : FinancialsEvent()
    data class DeleteOneOffCost(val costId: String) : FinancialsEvent()
    data object MessageShown : FinancialsEvent()
    /** SH-168: Try again after the month failed to load. */
    data object Retry : FinancialsEvent()
}

class FinancialsViewModel(
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val getRosterSummaryUseCase: GetRosterSummaryUseCase,
    private val financialRepository: FinancialRepository,
    private val sessionManager: WorkplaceSessionManager,
    /** For the workplace's currency; without it amounts show in PLN. */
    private val workplaceRepository: WorkplaceRepository? = null,
    private val addOneOffCostUseCase: AddOneOffCostUseCase? = null,
    private val deleteOneOffCostUseCase: DeleteOneOffCostUseCase? = null,
    private val observePlanActive: ObservePlanActiveUseCase? = null,
    /** SH-89: the top bar's month; without it the current month (or the latest one). */
    private val monthSelection: WorkplaceMonthSelection? = null,
    /** SH-115: the labour budget against the month's labour cost; without it none is shown. */
    private val getLabourBudget: GetLabourBudgetUseCase? = null,
    /**
     * SH-136: a change on the shared costs screen reads the month again (its ledger holds this
     * workplace's part), as the Money screen stays open behind it.
     */
    private val sharedCostChanges: SharedCostChanges? = null,
    /** SH-147: pay terms set elsewhere re-price the month: its figures are read again. */
    private val payTermsChanges: com.evoapps.evoshift.domain.budget.PayTermsChanges? = null,
    /**
     * SH-147: the month's labour cost worked out now from the shifts and their cost snapshots (as
     * the daily results, the dashboard and the roster show it) instead of the stored totals'
     * labour cost, which lags edits until the month is published again; without it, the totals'.
     */
    private val getMonthTrend: com.evoapps.evoshift.domain.usecase.finance.GetMonthTrendUseCase? = null,
    /** SH-152: the team's names for the explanations; without it people show as "no longer on the team". */
    private val getAssignableEmployees: com.evoapps.evoshift.domain.usecase.user.GetAssignableEmployeesUseCase? = null,
    /** SH-225: the comparison chips' reads; without them the month shows none. */
    getSalesComparison: com.evoapps.evoshift.domain.usecase.finance.GetSalesComparisonUseCase? = null,
    /** SH-225: today, for the week and month to date. */
    private val today: () -> kotlinx.datetime.LocalDate = { com.evoapps.evoshift.domain.util.AppClock.todayIn(TimeZone.currentSystemDefault()) }
) : ViewModel() {

    /** SH-225: the month's comparison chips (last year's reads kept between loads). */
    private val comparisonChips = SalesComparisonChips(getSalesComparison, workplaceRepository)

    private val _uiState = MutableStateFlow(FinancialsUiState())
    val uiState = _uiState.asStateFlow()

    /** SH-188: the selected month's totals listener (initialised before init's first load). */
    private val watching = JobSlot()
    private var watchedRosterId: String? = null

    init {
        observeSession()
        observeSharedCostChanges()
    }

    /** SH-136: the month on screen again, quietly (no spinner over its figures), after a shared cost changed. */
    private fun observeSharedCostChanges() {
        val shared = sharedCostChanges?.version?.drop(1)
        val pay = payTermsChanges?.version?.drop(1)
        val changes = listOfNotNull(shared, pay).takeIf { it.isNotEmpty() }?.merge() ?: return
        viewModelScope.launch {
            changes.collect {
                val state = _uiState.value
                val workplaceId = state.currentWorkplaceId ?: return@collect
                val rosterId = state.selectedRosterId ?: return@collect
                loadRosterData(workplaceId, rosterId)
            }
        }
    }

    private fun observeSession() {
        viewModelScope.launch {
            val selected = monthSelection?.month
            if (selected == null) {
                sessionManager.currentWorkplaceId.collectLatest { workplaceId ->
                    if (workplaceId != null) loadData(workplaceId, null)
                }
            } else {
                sessionManager.currentWorkplaceId.combine(selected) { workplaceId, month -> workplaceId to month }
                    .collectLatest { (workplaceId, month) -> if (workplaceId != null) loadData(workplaceId, month) }
            }
        }
    }

    fun onEvent(event: FinancialsEvent) {
        when (event) {
            is FinancialsEvent.SelectRoster -> {
                // With the shared month (SH-89) picking a roster picks its month everywhere.
                if (monthSelection != null) {
                    WorkplaceMonthSelection.monthOf(event.rosterId)?.let(monthSelection::selectMonth)
                    return
                }
                val workplaceId = sessionManager.currentWorkplaceId.value ?: return
                val state = _uiState.value
                if (state.selectedRosterId == event.rosterId && state.financials != null) return
                viewModelScope.launch {
                    loadRosterData(workplaceId, event.rosterId)
                }
            }
            is FinancialsEvent.SelectWorkplace -> {
                sessionManager.setWorkplaceId(event.workplaceId)
            }
            is FinancialsEvent.SaveOneOffCost -> saveOneOffCost(event.input, event.editingId)
            is FinancialsEvent.DeleteOneOffCost -> deleteOneOffCost(event.costId)
            FinancialsEvent.MessageShown -> _uiState.update { it.copy(error = null, successMessage = null) }
            FinancialsEvent.Retry -> {
                val workplaceId = sessionManager.currentWorkplaceId.value ?: return
                _uiState.update { it.copy(loadError = null, isLoading = true) }
                viewModelScope.launch { loadData(workplaceId, loadedMonth, force = true) }
            }
        }
    }

    // --- One-off expenses (SH-84) ------------------------------------------------------------

    /**
     * Optimistic: the expense shows at once (a placeholder id while new) and the month's other
     * costs move by its amount; a failed save puts both back and says why.
     */
    private fun saveOneOffCost(input: OneOffCostInput, editingId: String?) {
        val useCase = addOneOffCostUseCase ?: return
        val rosterId = _uiState.value.selectedRosterId ?: return
        if (editingId?.isPending == true || !_uiState.value.capabilities.shows(Gate.MONEY_EDITING)) return
        val before = _uiState.value
        val previous = editingId?.let { id -> before.costs.firstOrNull { it.id == id } }
        val shown = FixedCost(
            id = editingId ?: "$PENDING_ID_PREFIX${before.costs.size}-${input.name}",
            name = input.name.trim(),
            amount = input.amount,
            currency = input.currency,
            date = input.date,
            sourceType = CostSourceType.MANUAL_ONE_OFF,
            note = input.note?.trim()?.takeIf { it.isNotEmpty() },
            originRuleId = previous?.originRuleId,
            kind = input.kind,
            endDate = input.endDate?.takeIf { it > input.date }
        )
        _uiState.update {
            it.copy(
                costs = (it.costs.filterNot { cost -> cost.id == shown.id } + shown).sortedForLedger(),
                // SH-87: a cost moves the other costs, revenue the other revenue (and a changed kind both).
                financials = it.financials?.withItem(previous, -1)?.withItem(shown, +1)
            )
        }
        viewModelScope.launch {
            useCase(rosterId, input, editingId).fold(
                onSuccess = { saved ->
                    if (editingId == null) {
                        AppTelemetry.log(if (input.kind == LedgerKind.REVENUE) AnalyticsEvent.OneOffRevenueAdded else AnalyticsEvent.OneOffCostAdded)
                    }
                    _uiState.update {
                        it.copy(
                            costs = it.costs.map { cost -> if (cost.id == shown.id) saved else cost }.sortedForLedger(),
                            successMessage = UiText.Resource(Res.string.one_off_cost_saved)
                        )
                    }
                },
                onFailure = { e -> rollBack(before, e) }
            )
        }
    }

    private fun deleteOneOffCost(costId: String) {
        val useCase = deleteOneOffCostUseCase ?: return
        val rosterId = _uiState.value.selectedRosterId ?: return
        if (costId.isPending || !_uiState.value.capabilities.shows(Gate.MONEY_EDITING)) return
        val before = _uiState.value
        val cost = before.costs.firstOrNull { it.id == costId }?.takeIf { it.isOneOff } ?: return
        _uiState.update {
            it.copy(costs = it.costs.filterNot { c -> c.id == costId }, financials = it.financials?.withItem(cost, -1))
        }
        viewModelScope.launch {
            useCase(rosterId, costId).fold(
                onSuccess = { _uiState.update { it.copy(successMessage = UiText.Resource(Res.string.one_off_cost_deleted)) } },
                onFailure = { e -> rollBack(before, e) }
            )
        }
    }

    private fun rollBack(before: FinancialsUiState, e: Throwable) {
        _uiState.update { it.copy(costs = before.costs, financials = before.financials, error = e.toUiText()) }
    }

    /** The top bar's month last loaded (SH-89): another month reloads even at the same workplace. */
    private var loadedMonth: YearMonth? = null

    private suspend fun loadData(workplaceId: String, month: YearMonth?, force: Boolean = false) {
        if (!force && _uiState.value.currentWorkplaceId == workplaceId && _uiState.value.financials != null && month == loadedMonth) {
            return
        }
        loadedMonth = month
        // The previous workplace's figures stay until this one's arrive; the spinner is for an empty screen.
        _uiState.update { it.copy(isLoading = it.financials == null, currentWorkplaceId = workplaceId) }

        val user = getCurrentUserUseCase().first()
        if (user == null) {
            _uiState.update { it.copy(isLoading = false, error = UiText.Resource(Res.string.error_user_not_found)) }
            return
        }

        val workplaceIds = user.engagements.mapValues {
            it.value.cachedWorkplaceName?.takeIf { name -> name != "TBD" && name.isNotBlank() } .orEmpty()
        }

        val engagement = user.engagements[workplaceId]
        val workplaceName = engagement?.cachedWorkplaceName .orEmpty()
        // Read-only once the workplace's plan ended (SH-65): no one-off expenses to add or change.
        val capabilities = user.capabilitiesAt(workplaceId, observePlanActive.activeNow(workplaceId))
        _uiState.update { it.copy(capabilities = capabilities, availableWorkplaceIds = workplaceIds) }
        // The money is the owner's and financial managers' (roster_financials): nothing is read otherwise.
        if (!capabilities.shows(Gate.MONEY)) {
            _uiState.update {
                it.copy(isLoading = false, rosters = emptyList(), selectedRosterId = null, financials = null, rosterName = "", error = null)
            }
            return
        }

        // Find current or latest roster
        val now = now().toLocalDateTime(TimeZone.currentSystemDefault())
        val currentMonth = now.month.number
        val currentYear = now.year

        val rosters = try {
            getRosterSummaryUseCase(workplaceId).first().sortedByDescending { it.year * 100 + it.month }
        } catch (e: kotlinx.coroutines.CancellationException) {
            throw e
        } catch (e: Exception) {
            // SH-168: without the months nothing can be shown: Try again.
            _uiState.update { it.copy(isLoading = false, financials = null, loadError = e.toUiText()) }
            return
        }
        val targetRoster = if (month != null) rosters.of(month) else rosters.find { it.month == currentMonth && it.year == currentYear }
            ?: rosters.firstOrNull()
        val currency = workplaceRepository.currencyOf(workplaceId)
        val labourTarget = workplaceRepository.labourTargetOf(workplaceId)
        // SH-115: money only (VIEW_FINANCES, checked above); unreadable is no budget.
        val labourBudget = getLabourBudget?.invoke(workplaceId)?.getOrNull()
        // SH-152: whose shifts the explanations list (money only, checked above).
        val names = getAssignableEmployees?.invoke(workplaceId)?.getOrNull().orEmpty().associate { it.uid to it.displayName }

        _uiState.update {
            it.copy(
                rosters = rosters,
                workplaceName = workplaceName,
                currency = currency,
                labourTarget = labourTarget,
                labourBudget = labourBudget,
                names = names,
                availableWorkplaceIds = workplaceIds
            ) 
        }

        if (targetRoster != null) {
            loadRosterData(workplaceId, targetRoster.id)
        } else {
            _uiState.update { 
                it.copy(
                    isLoading = false, 
                    selectedRosterId = null,
                    // Never keep another workplace's figures on screen.
                    financials = null,
                    error = UiText.Resource(Res.string.financials_no_roster) 
                ) 
            }
        }
    }

    /**
     * SH-188: the month's totals are followed (the server recomputes them after takings or costs
     * are written, here or elsewhere): the figures are read again, quietly, behind those shown. So
     * the money is current when the person comes back from the daily results or the labour cost.
     */
    private fun watchTotals(workplaceId: String, rosterId: String) {
        if (watchedRosterId == rosterId) return
        watchedRosterId = rosterId
        watching.launch(viewModelScope) {
            financialRepository.observeRosterFinancials(rosterId).distinctUntilChanged().drop(1)
                .catch { e -> _uiState.update { it.copy(error = e.toUiText()) } }
                .collect { if (_uiState.value.selectedRosterId == rosterId) viewModelScope.launch { loadRosterData(workplaceId, rosterId) } }
        }
    }

    private suspend fun loadRosterData(workplaceId: String, rosterId: String) {
        if (!_uiState.value.capabilities.shows(Gate.MONEY)) return
        _uiState.update { it.copy(isLoading = it.financials == null, selectedRosterId = rosterId) }
        watchTotals(workplaceId, rosterId)
        
        val roster = _uiState.value.rosters.find { it.id == rosterId }
        val rosterName = if (roster != null) "${roster.month}/${roster.year}" else ""

        val (result, days) = kotlinx.coroutines.coroutineScope {
            val stored = async { financialRepository.getRosterFinancials(rosterId) }
            val live = async { getMonthTrend?.invoke(rosterId)?.getOrNull().orEmpty() }
            stored.await() to live.await()
        }
        // The month's ledger: the rules' costs and the one-off expenses (SH-84).
        val costs = financialRepository.getFixedCosts(rosterId).getOrDefault(emptyList()).sortedForLedger()
        // Another roster or workplace was selected meanwhile: its own load fills the screen.
        if (_uiState.value.selectedRosterId != rosterId) return
        _uiState.update { it.copy(costs = costs) }
        result.onSuccess { data ->
            _uiState.update {
                it.copy(
                    isLoading = false,
                    rosterName = rosterName,
                    financials = data.withLiveLabour(days),
                    days = days,
                    error = null,
                    loadError = null
                )
            }
            // SH-225: the month's sales against last week and last year (two range queries at most).
            val (year, month) = RosterIds.yearMonthOf(rosterId)
            val chips = comparisonChips.load(workplaceId, kotlinx.datetime.LocalDate(year, month, 1), today())
            if (_uiState.value.selectedRosterId == rosterId) _uiState.update { it.copy(salesComparison = chips) }
        }.onFailure { e ->
            _uiState.update { 
                it.copy(
                    isLoading = false,
                    rosterName = rosterName,
                    // Never keep the previous month's figures on screen once this one has answered.
                    financials = null,
                    days = emptyList(),
                    salesComparison = null,
                    // Not calculated yet is the empty state, not an error; SH-168: a failed read offers Try again.
                    error = null,
                    loadError = if (e is NotFoundException) null else e.toUiText()
                )
            }
        }
    }
}
